import React from "react";
import {
  Grid,
  Typography,
  FormControl,
  IconButton,
  Box,
  Paper,
  Button,
  InputAdornment,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SkeletonWrapper from "../Skeleton/SkeletonWrapper";
import { CustomSelect } from "../CustomFields/CustomSelect";
import CustomTimePicker from "../TimePicker/CustomTimePicker";
import dayjs, { Dayjs } from "dayjs";
import ClearIcon from "@mui/icons-material/Clear";
import { CustomTextField } from "../CustomFields/TextField";
import { createInputPropsForNumberField } from "../../shared/data";
import { selectOption } from "../../services/models/Context";

// ------------------------------------------------------------
// 🔹 Type Definitions
// ------------------------------------------------------------

interface SelectOption {
  value: string;
  label: string;
  locationId?: string;
  code?: string;
}

interface OutreachHospitalLocationDetail {
  startTime: Date | string | null;
  endTime: Date | string | null;
  displayOrder: number;
  parentDisplayOrder: number;
  isDeleted?: boolean;
  mode?: string;
}

interface OutreachHospitalLocationDataRow {
  location: SelectOption | null;
  locationId?: string | null;
  locationCode?: string | null;
  frequency: SelectOption | null;
  frequencyValue?: string | null;
  dayName: SelectOption | null;
  dayValue?: string | null;
  slotDuration: number | null;
  slotDurationType: selectOption | null;
  outreachHospitalLocationDetailColl: OutreachHospitalLocationDetail[];
  isDeleted?: boolean;
  displayOrder: number;
  mode?: string;
}
interface OptionType {
  value: string;
  label: string;
}
interface OutReachHoursSectionProps {
  methods: UseFormReturn<{
    outreachHospitalLocationData: OutreachHospitalLocationDataRow[];
  }>;
  showSkeleton: boolean;
  teamMemberLocationData: SelectOption[];
  outReachLocationData: SelectOption[];
  daysOfWeek: string[];
  outreachHospitalLocationDetailColl: OutreachHospitalLocationDataRow[];
  handleDetailFieldChange?: (...args: any[]) => void; // optional callback from parent
  frequencyOptions: SelectOption[];
  durationTypeOptions: OptionType[];
}
const prProps = createInputPropsForNumberField(3, 0, false);
// ------------------------------------------------------------
// 🔹 Component
// ------------------------------------------------------------
const OutReachHoursSection: React.FC<OutReachHoursSectionProps> = ({
  methods,
  showSkeleton,
  teamMemberLocationData,
  daysOfWeek,
  outreachHospitalLocationDetailColl,
  handleDetailFieldChange,
  frequencyOptions,
  durationTypeOptions,
  outReachLocationData,
}) => {
  const allRows: OutreachHospitalLocationDataRow[] =
    methods.getValues("outreachHospitalLocationData") || [];

  const visibleRows = allRows.filter((r) => !r.isDeleted);

  // Initialize if empty
  // if (visibleRows.length === 0 && allRows.length === 0) {
  //   const initialRow: OutreachHospitalLocationDataRow = {
  //     location: null,
  //     dayName: null,
  //     frequency: null,
  //     outreachHospitalLocationDetailColl: [
  //       {
  //         startTime: dayjs().hour(9).minute(0).toDate(),
  //         endTime: dayjs().hour(12).minute(0).toDate(),
  //         displayOrder: 0,
  //         parentDisplayOrder: 0,
  //         mode: "I",
  //         isDeleted: false,
  //       },
  //     ],
  //     isDeleted: false,
  //     displayOrder: 0,
  //     slotDuration: null,
  //     slotDurationType: null,
  //   };
  //   methods.setValue("outreachHospitalLocationData", [initialRow], {
  //     shouldValidate: true,
  //   });
  // }

  // ➕ Add new time slot
  const handleAddTimeSlot = (rowDisplayOrder: number) => {
    const path = `outreachHospitalLocationData.${rowDisplayOrder}.outreachHospitalLocationDetailColl`;
    const existingSlots: OutreachHospitalLocationDetail[] =
      methods.getValues(path) || [];

    const maxDisplayOrder =
      existingSlots.length > 0
        ? Math.max(...existingSlots.map((s) => s.displayOrder || 0))
        : 0;

    const parentDisplayOrder = methods.getValues(
      `outreachHospitalLocationData.${rowDisplayOrder}.displayOrder`
    );

    const newSlot: OutreachHospitalLocationDetail = {
      startTime: dayjs().hour(9).minute(0).toDate(),
      endTime: dayjs().hour(12).minute(0).toDate(),
      displayOrder: maxDisplayOrder + 1,
      parentDisplayOrder,
      isDeleted: false,
      mode: "I",
    };

    methods.setValue(path, [...existingSlots, newSlot], {
      shouldValidate: true,
    });
  };

  // ❌ Remove time slot
  const handleRemoveTimeSlot = (
    rowDisplayOrder: number,
    slotDisplayOrder: number
  ) => {
    const path = `outreachHospitalLocationData.${rowDisplayOrder}.outreachHospitalLocationDetailColl`;
    const existingSlots: OutreachHospitalLocationDetail[] =
      methods.getValues(path) || [];

    const updatedSlots = existingSlots.map((slot) =>
      slot.displayOrder === slotDisplayOrder
        ? { ...slot, isDeleted: true, mode: "D" }
        : slot
    );

    methods.setValue(path, updatedSlots, { shouldValidate: true });
  };

  // ❌ Remove outreach row
  const handleRemoveRow = (rowIndex: number) => {
    const path = `outreachHospitalLocationData`;
    const existingRows: OutreachHospitalLocationDataRow[] =
      methods.getValues(path) || [];

    const updatedRows = existingRows.map((row) =>
      row.displayOrder === rowIndex
        ? {
            ...row,
            isDeleted: true,
            mode: "D",
            outreachHospitalLocationDetailColl:
              row.outreachHospitalLocationDetailColl.map((slot) => ({
                ...slot,
                isDeleted: true,
                mode: "D",
              })),
          }
        : row
    );

    methods.setValue(path, updatedRows, { shouldValidate: true });
  };

  // ➕ Add new outreach row
  const handleAddNewOutReachLocationRow = () => {
    const path = `outreachHospitalLocationData`;
    const existingRows: OutreachHospitalLocationDataRow[] =
      methods.getValues(path) || [];

    const maxDisplayOrder = existingRows?.length
      ? Math.max(...existingRows.map(r => r.displayOrder)) + 1
      : 0;
    const customValue =
      methods.getValues("availabilityData.customValue") || null;
    const customType = methods.getValues("availabilityData.customType") || null;

    const newRow: OutreachHospitalLocationDataRow = {
      location: null,
      dayName: null,
      frequency: null,
      outreachHospitalLocationDetailColl: [
        {
          startTime: dayjs().hour(9).minute(0).toDate(),
          endTime: dayjs().hour(12).minute(0).toDate(),
          displayOrder: 0,
          parentDisplayOrder: maxDisplayOrder,
          isDeleted: false,
          mode: "I",
        },
      ],
      isDeleted: false,
      displayOrder: maxDisplayOrder,
      slotDuration: customValue,
      slotDurationType: customType,
    };

    methods.setValue(path, [...existingRows, newRow], { shouldValidate: true });
  };

  // ------------------------------------------------------------
  // 🔹 JSX Rendering
  // ------------------------------------------------------------
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6">Outreach OPD Hours</Typography>
        <Typography className="font-size-12">
          Set when you’re in Outreach OPD Location
        </Typography>
      </Grid>

      <Grid item xs={12}>
        {
          // outreachHospitalLocationDetailColl.length > 0 ? (
          outreachHospitalLocationDetailColl
            .filter((x) => !x.isDeleted)
            .map((row) => (
              <Paper
                key={row.displayOrder}
                elevation={1}
                sx={{ padding: "16px 0px" }}
              >
                <Grid container spacing={1} alignItems="top">
                  {/* Location */}
                  <Grid item xs={12} sm={6} md={4} lg={2.5} xl={2.5}>
                    <FormControl fullWidth>
                      <SkeletonWrapper isLoading={showSkeleton}>
                        <Controller
                          control={methods.control}
                          name={`outreachHospitalLocationData.${row.displayOrder}.locationId`}
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              id={`outreachHospitalLocationData.${row.displayOrder}.locationId`}
                              FieldName={`outreachHospitalLocationData.${row.displayOrder}.locationId`}
                              isNotCreatable
                              options={outReachLocationData}
                              placeholder="Location"
                              handleChange={(e: any) => {
                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.locationId`,
                                  e || null,
                                  { shouldValidate: true }
                                );

                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.locationName`,
                                  e?.label || null,
                                  { shouldValidate: true }
                                );

                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.locationCode`,
                                  e?.code || null
                                );
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  {/* Frequency */}
                  <Grid item xs={6} sm={3} md={4} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <SkeletonWrapper isLoading={showSkeleton}>
                        <Controller
                          control={methods.control}
                          name={`outreachHospitalLocationData.${row.displayOrder}.frequency`}
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              id="frequency"
                              FieldName="frequency"
                              isNotCreatable
                              options={frequencyOptions}
                              placeholder="Frequency"
                              handleChange={(e: SelectOption | null) => {
                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.frequency`,
                                  e || null,
                                  { shouldValidate: true }
                                );
                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.frequencyValue`,
                                  e?.value || null,
                                  { shouldValidate: true }
                                );
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  {/* Day */}
                  <Grid item xs={6} sm={3} md={4} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <SkeletonWrapper isLoading={showSkeleton}>
                        <Controller
                          control={methods.control}
                          name={`outreachHospitalLocationData.${row.displayOrder}.dayName`}
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              id="dayName"
                              FieldName="dayName"
                              isNotCreatable
                              options={daysOfWeek.map((d) => ({
                                value: d.toUpperCase(),
                                label: d,
                              }))}
                              placeholder="Day"
                              handleChange={(e: SelectOption | null) => {
                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.dayName`,
                                  e || null,
                                  { shouldValidate: true }
                                );
                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.dayValue`,
                                  e?.value || null
                                );
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  {/* Duration Slots */}
                  <Grid item xs={12} sm={5} md={3.5} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="Enter value"
                          fullWidth
                          id={`slotDuration-${row.displayOrder}`}
                          size="small"
                          type="number"
                          inputProps={prProps}
                          {...methods.register(
                            `outreachHospitalLocationData.${row.displayOrder}.slotDuration`,
                            {
                              onChange(event) {
                                methods.setValue(
                                  `outreachHospitalLocationData.${row.displayOrder}.slotDuration`,
                                  event.target.value,
                                  { shouldValidate: true }
                                );
                              },
                            }
                          )}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{ display: "flex", minWidth: "120px" }}
                                >
                                  <Box
                                    className="input-end-andorment-border"
                                    sx={{ width: "100%" }}
                                  >
                                    <Controller
                                      control={methods.control}
                                      name={`outreachHospitalLocationData.${row.displayOrder}.slotDurationType`}
                                      render={({ field }) => (
                                        <CustomSelect
                                          {...field}
                                          isBorderNone
                                          isNotCreatable
                                          isIndicatorSeparator={false}
                                          isSearchable={false}
                                          id={`slotDurationType-${row.displayOrder}`}
                                          FieldName="slotDurationType"
                                          options={durationTypeOptions}
                                          handleChange={(
                                            e: OptionType | null
                                          ) => {
                                            const curr =
                                              methods.getValues()
                                                ?.outreachHospitalLocationData?.[
                                                row.displayOrder
                                              ]?.slotDurationType?.value;
                                            if (curr !== e?.value) {
                                              methods.setValue(
                                                `outreachHospitalLocationData.${row.displayOrder}.slotDurationType`,
                                                e?.value ? e : null,
                                                { shouldValidate: true }
                                              );
                                            }
                                          }}
                                        />
                                      )}
                                    />
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "30px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                              paddingLeft: 0,
                            },
                            "& input": {
                              textAlign: "end",
                            },
                          }}
                          error={
                            !!methods.formState.errors
                              ?.outreachHospitalLocationData?.[row.displayOrder]
                              ?.slotDuration
                          }
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  {/* Time Slots */}
                  <Grid item xs={11} sm={6} md={4} lg={3} xl={3}>
                    <Box sx={{ width: "100%" }}>
                      {row.outreachHospitalLocationDetailColl
                        .filter((x) => !x.isDeleted)
                        .map((slot, slotIndex, arr) => (
                          <Box
                            key={slot.displayOrder}
                            sx={{
                              width: "100%",
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {/* Start Time */}
                            <FormControl sx={{ mr: 1 }}>
                              <SkeletonWrapper isLoading={showSkeleton}>
                                <Controller
                                  control={methods.control}
                                  name={`outreachHospitalLocationData.${row.displayOrder}.outreachHospitalLocationDetailColl.${slot.displayOrder}.startTime`}
                                  render={({ field }) => (
                                    <CustomTimePicker
                                      {...field}
                                      value={
                                        slot?.startTime
                                          ? dayjs(slot.startTime)
                                          : null
                                      }
                                      onTimeChange={(newTime: Dayjs | null) =>
                                        methods.setValue(
                                          `outreachHospitalLocationData.${row.displayOrder}.outreachHospitalLocationDetailColl.${slot.displayOrder}.startTime`,
                                          newTime,
                                          { shouldValidate: true }
                                        )
                                      }
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                            </FormControl>

                            {/* End Time */}
                            <FormControl sx={{ mr: 1 }}>
                              <SkeletonWrapper isLoading={showSkeleton}>
                                <Controller
                                  control={methods.control}
                                  name={`outreachHospitalLocationData.${row.displayOrder}.outreachHospitalLocationDetailColl.${slot.displayOrder}.endTime`}
                                  render={({ field }) => (
                                    <CustomTimePicker
                                      {...field}
                                      value={
                                        slot?.endTime
                                          ? dayjs(slot.endTime)
                                          : null
                                      }
                                      onTimeChange={(newTime: Dayjs | null) =>
                                        methods.setValue(
                                          `outreachHospitalLocationData.${row.displayOrder}.outreachHospitalLocationDetailColl.${slot.displayOrder}.endTime`,
                                          newTime,
                                          { shouldValidate: true }
                                        )
                                      }
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                            </FormControl>

                            {/* Add / Remove */}
                            {arr.length > 1 ? (
                              <ClearIcon
                                sx={{ color: "red", cursor: "pointer", mr: 1 }}
                                onClick={() =>
                                  handleRemoveTimeSlot(
                                    row.displayOrder,
                                    slot.displayOrder
                                  )
                                }
                              />
                            ) : (
                              <Box sx={{ width: 24 }} />
                            )}
                            {slotIndex === 0 ? (
                              <AddIcon
                                sx={{
                                  cursor: "pointer",
                                  color: "primary.main",
                                }}
                                onClick={() =>
                                  handleAddTimeSlot(row.displayOrder)
                                }
                              />
                            ) : (
                              <Box sx={{ width: 24 }} />
                            )}
                          </Box>
                        ))}
                    </Box>
                  </Grid>

                  <Grid item xs={1} sm={1} md={0.5} lg={0.5} xl={0.5}>
                    <IconButton
                      sx={{
                        p: 0.5,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                      onClick={() => handleRemoveRow(row.displayOrder)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))
          // ) : (
          //   <Box textAlign="center" py={3}>
          //     <Typography variant="body2" color="text.secondary">
          //       No record available
          //     </Typography>
          //   </Box>
          // )
        }
      </Grid>

      {/* ➕ Add New Row */}
      <Grid item xs={12}>
        <Button variant="text" onClick={handleAddNewOutReachLocationRow}>
          <AddIcon className="add-icon" />
          <span className="add-icon"> Add New Outreach Location</span>
        </Button>
      </Grid>
    </>
  );
};

export default OutReachHoursSection;
